// src/pages/PaymentReturn.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../api";

const STATUS = {
  VERIFYING: "verifying",
  SUCCESS: "success",
  FAILED: "failed",
  ERROR: "error",
};

export default function PaymentReturn() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const bookingNo = (params.get("bookingNo") || "").trim();

  const [status, setStatus] = useState(STATUS.VERIFYING);
  const [message, setMessage] = useState("Verifying your payment...");

  const cancelledRef = useRef(false);
  const redirectTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const attemptsRef = useRef(0);
  const verifyStartedRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    return () => {
      cancelledRef.current = true;

      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!bookingNo) {
      setStatus(STATUS.ERROR);
      setMessage("Missing booking number.");
      return;
    }

    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken") || "";

    const sessionKey = `rb_genie_verified_${bookingNo}`;
    const sessionStatusKey = `rb_genie_verified_status_${bookingNo}`;

    if (sessionStorage.getItem(sessionKey) === "done") {
      navigate(`/download-ticket?bookingNo=${encodeURIComponent(bookingNo)}`, {
        replace: true,
      });
      return;
    }

    if (verifyStartedRef.current) return;
    verifyStartedRef.current = true;
    attemptsRef.current = 0;

    const goFailed = () => {
      redirectTimerRef.current = setTimeout(() => {
        navigate(`/payment-failed?bookingNo=${encodeURIComponent(bookingNo)}`, {
          replace: true,
        });
      }, 1200);
    };

    const verifyOnce = async () => {
      if (cancelledRef.current) return;

      try {
        attemptsRef.current += 1;

        const { data } = await apiClient.get(`/genie/verify/${bookingNo}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (cancelledRef.current) return;

        if (data?.paid) {
          try {
            sessionStorage.setItem(sessionKey, "done");
            sessionStorage.setItem(sessionStatusKey, "paid");
          } catch {}

          setStatus(STATUS.SUCCESS);
          setMessage("Payment successful. Redirecting to your ticket...");

          redirectTimerRef.current = setTimeout(() => {
            navigate(
              `/download-ticket?bookingNo=${encodeURIComponent(bookingNo)}`,
              {
                replace: true,
              }
            );
          }, 1200);

          return;
        }

        // payment not yet marked paid - retry a few times for webhook delay
        if (attemptsRef.current < 6) {
          setStatus(STATUS.VERIFYING);
          setMessage("Payment is being confirmed. Please wait...");

          retryTimerRef.current = setTimeout(() => {
            if (!cancelledRef.current) verifyOnce();
          }, 2000);

          return;
        }

        try {
          sessionStorage.setItem(sessionStatusKey, "failed");
        } catch {}

        setStatus(STATUS.FAILED);
        setMessage("Payment could not be confirmed.");
        goFailed();
      } catch (err) {
        if (cancelledRef.current) return;

        // retry a few times because webhook/update may lag
        if (attemptsRef.current < 6) {
          setStatus(STATUS.VERIFYING);
          setMessage("Checking payment status...");

          retryTimerRef.current = setTimeout(() => {
            if (!cancelledRef.current) verifyOnce();
          }, 2000);

          return;
        }

        try {
          sessionStorage.setItem(sessionStatusKey, "error");
        } catch {}

        setStatus(STATUS.ERROR);
        setMessage(
          err?.response?.data?.message ||
            "Payment verification failed. Redirecting..."
        );

        redirectTimerRef.current = setTimeout(() => {
          navigate(`/payment-failed?bookingNo=${encodeURIComponent(bookingNo)}`, {
            replace: true,
          });
        }, 1400);
      }
    };

    const handlePageShow = (event) => {
      if (!bookingNo) return;

      const alreadyDone = sessionStorage.getItem(sessionKey) === "done";
      if (event.persisted && alreadyDone) {
        navigate(`/download-ticket?bookingNo=${encodeURIComponent(bookingNo)}`, {
          replace: true,
        });
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    verifyOnce();

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      verifyStartedRef.current = false;

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }

      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, [bookingNo, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-center">
        <div className="mb-4">
          {status === STATUS.VERIFYING && (
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-gray-200 border-t-rose-600 animate-spin" />
          )}

          {status === STATUS.SUCCESS && (
            <div className="w-12 h-12 mx-auto rounded-full bg-green-100 flex items-center justify-center text-2xl">
              ✅
            </div>
          )}

          {(status === STATUS.FAILED || status === STATUS.ERROR) && (
            <div className="w-12 h-12 mx-auto rounded-full bg-red-100 flex items-center justify-center text-2xl">
              ⚠️
            </div>
          )}
        </div>

        <h1 className="text-xl font-bold text-gray-900">
          {status === STATUS.VERIFYING && "Processing Payment"}
          {status === STATUS.SUCCESS && "Payment Successful"}
          {status === STATUS.FAILED && "Payment Not Confirmed"}
          {status === STATUS.ERROR && "Verification Error"}
        </h1>

        <p className="text-sm text-gray-600 mt-3">{message}</p>

        {bookingNo ? (
          <p className="text-xs text-gray-400 mt-4">
            Ref: <span className="font-medium">{bookingNo}</span>
          </p>
        ) : null}

        {(status === STATUS.FAILED || status === STATUS.ERROR) && (
          <div className="mt-6 grid grid-cols-1 gap-3">
            <button
              onClick={() =>
                navigate(
                  `/payment-failed?bookingNo=${encodeURIComponent(bookingNo)}`,
                  {
                    replace: true,
                  }
                )
              }
              className="w-full py-3 rounded-lg text-white font-semibold bg-rose-600 hover:bg-rose-700"
            >
              Go to Payment Failed
            </button>

            <button
              onClick={() => navigate("/my-bookings")}
              className="w-full py-3 rounded-lg font-semibold bg-white border border-gray-300 hover:bg-gray-50"
            >
              My Bookings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
